import {
  Excalidraw,
  convertToExcalidrawElements,
} from '@excalidraw/excalidraw';
import '@excalidraw/excalidraw/index.css';
import { Loader2, XCircle, Mic, MicOff, Volume2 } from 'lucide-react';
import { Button } from '@ai-tutor/ui';
import { useState, useEffect, useMemo, useRef } from 'react';
import { cn } from '@/lib/utils';

interface DoubtSolvingCanvasProps {
  scene: any[] | null;
  isLoading: boolean;
  onCancel: () => void;
  isPlaying?: boolean;
  isRecording?: boolean;
  hasPermission?: boolean | null;
  startRecording?: () => Promise<void>;
  stopRecording?: () => void;
  requestPermission?: () => Promise<boolean>;
}

export function DoubtSolvingCanvas({
  scene,
  isLoading,
  onCancel,
  isPlaying = false,
  isRecording = false,
  hasPermission = null,
  startRecording,
  stopRecording,
  requestPermission,
}: DoubtSolvingCanvasProps) {
  const [excalidrawAPI, setExcalidrawAPI] = useState<any>(null);
  const [sceneKey, setSceneKey] = useState<string>('empty');
  const previousSceneRef = useRef<string>('');

  // Update key only when scene content actually changes
  useEffect(() => {
    const currentSceneHash = scene ? JSON.stringify(scene) : 'empty';

    if (currentSceneHash !== previousSceneRef.current) {
      previousSceneRef.current = currentSceneHash;
      setSceneKey(`scene-${Date.now()}`);
    }
  }, [scene]);

  // Handle microphone toggle
  const handleMicToggle = async () => {
    if (!startRecording || !stopRecording) return;

    if (isRecording) {
      stopRecording();
    } else {
      // Request permission first if not granted
      if (hasPermission === null && requestPermission) {
        const granted = await requestPermission();
        if (!granted) {
          return; // Permission denied
        }
      }

      await startRecording();
    }
  };

  // Convert skeleton elements to proper Excalidraw elements
  // Memoize to prevent re-creating array on every render (fixes infinite loop)
  const excalidrawElements = useMemo(() => {
    const elements = scene ? convertToExcalidrawElements(scene) : [];
    return elements;
  }, [scene]); // Only recalculate when scene actually changes

  // Center and scale the content to fill viewport after API is ready
  // Only run when scene key changes (new scene loaded)
  useEffect(() => {
    if (excalidrawAPI && excalidrawElements.length > 0) {
      // Use a slight delay to ensure Excalidraw is fully rendered
      setTimeout(() => {
        excalidrawAPI.scrollToContent(excalidrawElements, {
          fitToViewport: true, // Scale content to fill viewport (no zoom limit)
          viewportZoomFactor: 0.7, // Content should cover 70% of viewport
          animate: false,
        });
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneKey]); // Only run when scene actually changes, not on every excalidrawAPI/elements update

  return (
    <div className='doubt-canvas-host relative h-full w-full bg-card'>
      {/* Loading Overlay */}
      {isLoading && (
        <div className='absolute inset-0 z-50 flex flex-col items-center justify-center bg-card/90'>
          <Loader2 className='mb-4 h-12 w-12 animate-spin text-brand-600 dark:text-primary' />
          <p className='mb-2 font-medium text-foreground'>
            Generating explanation...
          </p>
          <Button
            variant='outline'
            size='sm'
            onClick={onCancel}
            className='mt-2'
          >
            <XCircle className='mr-2 h-4 w-4' />
            Cancel
          </Button>
        </div>
      )}

      {/* Excalidraw Canvas */}
      <Excalidraw
        excalidrawAPI={api => setExcalidrawAPI(api)}
        key={sceneKey} // Force complete remount only when scene content changes
        initialData={{
          elements: excalidrawElements,
          appState: {
            viewBackgroundColor: '#ffffff',
            currentItemFontFamily: 1,
            zenModeEnabled: true,
          },
        }}
        viewModeEnabled={true} // Read-only mode
        zenModeEnabled={true} // Hide UI chrome for clean view
        gridModeEnabled={false}
        theme='light'
      />

      {/* Audio Status Indicator */}
      {(isPlaying || isRecording) && (
        <div className='absolute left-1/2 top-6 z-40 -translate-x-1/2 transform'>
          <div className='flex items-center gap-3 rounded-full border border-border bg-card px-6 py-3 shadow-lg'>
            {isPlaying && (
              <>
                <Volume2 className='h-5 w-5 animate-pulse text-brand-600 dark:text-primary' />
                <span className='text-sm font-medium text-foreground'>
                  Xonera is speaking...
                </span>
              </>
            )}
            {isRecording && !isPlaying && (
              <>
                <div className='h-3 w-3 animate-pulse rounded-full bg-red-500' />
                <span className='text-sm font-medium text-foreground'>
                  Listening...
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Microphone Button */}
      <div className='absolute bottom-8 left-1/2 z-40 -translate-x-1/2 transform'>
        <Button
          onClick={handleMicToggle}
          size='lg'
          className={cn(
            // Note: do NOT use `transition-all` here. The base Button
            // already has `transition-colors`, and adding transition-all
            // makes the *opacity* change track too - which fights with
            // `animate-pulse` (a 2s opacity keyframe), producing a
            // visible stutter that looks like flicker.
            'h-16 w-16 rounded-full shadow-2xl',
            isRecording
              ? 'animate-pulse bg-red-600 hover:bg-red-700'
              : 'bg-brand-600 hover:bg-brand-700 dark:bg-primary dark:hover:bg-primary'
          )}
          disabled={hasPermission === false}
        >
          {isRecording ? (
            <MicOff className='h-6 w-6 text-white' />
          ) : (
            <Mic className='h-6 w-6 text-white' />
          )}
        </Button>

        {/* Permission denied message */}
        {hasPermission === false && (
          <div className='absolute left-1/2 top-full mt-2 -translate-x-1/2 transform whitespace-nowrap'>
            <p className='rounded-full bg-card px-3 py-1 text-xs text-red-600 shadow-md dark:text-red-400'>
              Microphone permission denied
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
